import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

const LS_USER  = 'user';   // consolidated user payload (JSON)
const LS_TOKEN = 'token';  // JWT
// legacy keys (kept for compatibility / migration)
const LS_USER_EMAIL = 'userEmail';
const LS_USER_ID    = 'userId';

// Helper: safe normalize without mixing ?? and ||
function normalizeUser(u) {
  if (!u) return null;

  // id resolution: prefer u.id, then u.userId, then u.sub
  let rawId = null;
  if (u.id !== undefined && u.id !== null) rawId = u.id;
  else if (u.userId !== undefined && u.userId !== null) rawId = u.userId;
  else if (u.sub !== undefined && u.sub !== null) rawId = u.sub;

  let id = null;
  if (rawId !== undefined && rawId !== null) {
    id = isNaN(rawId) ? rawId : Number(rawId);
  }

  const email = (u.email !== undefined && u.email !== null) ? u.email : null;

  const firstName = (u.firstName !== undefined && u.firstName !== null)
    ? u.firstName
    : '';

  const lastName = (u.lastName !== undefined && u.lastName !== null)
    ? u.lastName
    : '';

  let name = '';
  if (u.name) {
    name = u.name;
  } else {
    const combined = [firstName, lastName].filter(Boolean).join(' ').trim();
    name = combined ? combined : '';
  }

  return { id, email, firstName, lastName, name };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // { id, email, firstName, lastName, name }
  const [token, setToken] = useState(null); // jwt string

  // Rehydrate (supports legacy keys and migrates them)
  useEffect(() => {
    const storedToken = localStorage.getItem(LS_TOKEN);
    const storedUserJson = localStorage.getItem(LS_USER);
    const legacyEmail = localStorage.getItem(LS_USER_EMAIL);
    const legacyId = localStorage.getItem(LS_USER_ID);

    if (storedUserJson) {
      try {
        const parsed = JSON.parse(storedUserJson);
        setUser(normalizeUser(parsed));
      } catch {
        setUser(null);
      }
    } else if (legacyEmail && legacyId) {
      const migrated = normalizeUser({ id: legacyId, email: legacyEmail });
      setUser(migrated);
      localStorage.setItem(LS_USER, JSON.stringify(migrated));
      // keep legacy keys for now; safe to remove later
    }

    if (storedToken) setToken(storedToken);
  }, []);

  // Optional: auto-logout when token expires
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      const exp = decoded?.exp;
      if (!exp) return;

      const ms = exp * 1000 - Date.now();
      if (ms <= 0) {
        logout();
        return;
      }
      const timer = setTimeout(logout, ms + 2000);
      return () => clearTimeout(timer);
    } catch {
      // if decode fails, ignore
    }
  }, [token]);

  // Backward-compatible login:
  // - New style: login(userObj, jwtToken)
  // - Legacy:    login(email, id)
  const login = (arg1, arg2) => {
    // New style: first arg is an object
    if (arg1 && typeof arg1 === 'object') {
      const userObj = normalizeUser(arg1);
      const jwtToken = arg2 || null;

      setUser(userObj);
      if (jwtToken) setToken(jwtToken);

      localStorage.setItem(LS_USER, JSON.stringify(userObj));
      if (jwtToken) localStorage.setItem(LS_TOKEN, jwtToken);

      // keep legacy keys to avoid breaking older code paths
      if (userObj && userObj.email) localStorage.setItem(LS_USER_EMAIL, userObj.email);
      if (userObj && userObj.id !== undefined && userObj.id !== null) {
        localStorage.setItem(LS_USER_ID, String(userObj.id));
      }
      return;
    }

    // Legacy style: (email, id)
    const legacyEmail = arg1 ?? null;
    const legacyId = arg2 ?? null;
    const legacyUser = normalizeUser({ email: legacyEmail, id: legacyId });

    setUser(legacyUser);
    localStorage.setItem(LS_USER, JSON.stringify(legacyUser));
    localStorage.setItem(LS_USER_EMAIL, legacyUser.email || '');
    localStorage.setItem(LS_USER_ID, legacyUser.id !== undefined && legacyUser.id !== null ? String(legacyUser.id) : '');
    // token handling unchanged for legacy path
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_USER_EMAIL);
    localStorage.removeItem(LS_USER_ID);
  };

  const value = useMemo(() => ({
    user,
    token,
    login,
    logout,
    isAuthenticated: Boolean(token) || Boolean(user),
  }), [user, token]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
